/*

  Tile.ts

  Tile component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { EXTRA_WIDE_LETTERS, IBaseGame, IView, WIDE_LETTERS } from "./types";
import { VnodeAttrs, ComponentFunc, m, MithrilMouseEvent, MithrilTouchEvent } from "./mithril";
import { nbsp } from "./util";
import { startDrag } from "./dragdrop";

interface IAttributes {
  view: IView;
  game: IBaseGame;
  coord: string;
  opponent: boolean;
}

export const Tile: ComponentFunc<IAttributes> = (initialVnode) => {
  // Display a tile on the board or in the rack
  const { view, game, coord } = initialVnode.attrs;

  const dragHandler = (ev: MithrilMouseEvent | MithrilTouchEvent) => {
    // Start a drag-and-drop process, for mouse or touch interaction
    startDrag(ev, (_, target) => {
      // Drop handler
      if (!game) return;
      const id = target.id;
      if (!id) return;
      let targetCoord: string = "";
      if (id === "board-background") {
        // Drop on the background: transfer back to the rack
        targetCoord = "R1";
      } else if (id.startsWith("sq_")) {
        // Drop on a board square
        targetCoord = id.slice(3);
      }
      if (targetCoord) {
        try {
          game.attemptMove(coord, targetCoord);
          view.updateScale();
          // Make sure that Mithril draws the updated state
          m.redraw();
        } catch (e) {
          // Something went wrong: display a console error
          console.error(e);
        }
      }
    });
    ev.redraw = false;
    return false;
  };

  return {
    view: (vnode) => {
      if (!game) return undefined;
      const { opponent } = vnode.attrs;
      const isRackTile = coord[0] === 'R';
      // A single tile, on the board or in the rack
      const t = game.tiles[coord];
      let classes = [".netskrafl-tile"];
      let attrs: VnodeAttrs = {};
      if (t.tile === '?')
        classes.push("netskrafl-blanktile");
      if (EXTRA_WIDE_LETTERS.includes(t.letter))
        // Extra wide letter: handle specially
        classes.push("netskrafl-extra-wide");
      else if (WIDE_LETTERS.includes(t.letter))
        // Wide letter: handle specially
        classes.push("netskrafl-wide");
      if (isRackTile || t.draggable) {
        // Rack tile, or at least a draggable one
        classes.push(opponent ? "netskrafl-freshtile" : "netskrafl-racktile");
        if (isRackTile && game.showingDialog === "exchange") {
          // Rack tile, and we're showing the exchange dialog
          if (t.xchg)
            // Chosen as an exchange tile
            classes.push("netskrafl-xchgsel");
          // Exchange dialog is live: add a click handler for the
          // exchange state
          attrs.onclick = (ev: Event) => {
            // Toggle the exchange status of this tile
            t.xchg = !t.xchg;
            ev.preventDefault();
          };
        }
      } else if (t.freshtile) {
        // A fresh tile on the board that has
        // just been played by the opponent
        classes.push("netskrafl-freshtile");
      }
      if (t.index) {
        // Make fresh or highlighted tiles appear sequentally by animation
        const ANIMATION_STEP = 150; // Milliseconds
        const delay = (t.index * ANIMATION_STEP).toString() + "ms";
        attrs.style = `animation-delay: ${delay}`;
      }
      if (coord === game.selectedSq)
        // Currently selected square
        classes.push("sel"); // Blinks red
      if (t.highlight !== undefined) {
        // highlight0 is the local player color
        // highlight1 is the remote player color
        classes.push("highlight" + t.highlight);
        /*
        if (t.player == parseInt(t.highlight))
          // This tile was originally laid down by the other player
          classes.push("dim");
        */
      }
      if (t.draggable && game.allowDragDrop()) {
        attrs.onmousedown = dragHandler;
        attrs.ontouchstart = dragHandler;
        /*
        attrs.onclick = (ev: MouseEvent) => {
          // When clicking a tile, make it selected (blinking)
          if (coord === game.selectedSq)
            // Clicking again: deselect
            game.selectedSq = null;
          else
            game.selectedSq = coord;
          ev.stopPropagation();
          return false;
        };
        */
      }
      return m(classes.join("."), attrs,
        [t.letter === " " ? nbsp() : t.letter, m(".letterscore", t.score)]
      );
    }
  };
}
